import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EmployeeActionsComponent } from 'src/app/cell-renderer/employee-actions/employee-actions.component';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';
import { EmployeeService } from 'src/app/services/employee.service';
import { PayrollPeriodService } from 'src/app/services/payroll-period.service';
import { PermissionService } from 'src/app/services/permission.service';
import { UtilsService } from 'src/app/services/utils.service';
import { TerminationEvent, TerminationModalComponent } from '../termination-modal/termination-modal.component';
import { CierreNominaEvent, CierreNominaModalComponent } from '../cierre-nomina-modal/cierre-nomina-modal.component';

interface AvailabilityMeta {
    label: string;
    bg: string;
    color: string;
}

@Component({
    selector: 'app-employee-list',
    templateUrl: './employee-list.component.html',
    styleUrls: ['./employee-list.component.css']
})
export class EmployeeListComponent implements OnInit {

    @ViewChild('terminationModal') terminationModal!: TerminationModalComponent;
    @ViewChild('cierreNominaModal') cierreNominaModal!: CierreNominaModalComponent;

    /**
     * Contexto expuesto a los cell renderers de ag-grid (ej. EmployeeActionsComponent)
     * para que puedan invocar métodos de este componente padre, ya que ese renderer
     * se declara en AppModule y no tiene acceso directo al template de este módulo.
     */
    gridContext = { componentParent: this };

    /** Refleja el estado del diseño "Nómina total cerrada": true mientras exista un periodo abierto. */
    hasOpenPeriod = true;

    /** El ícono "Nómina del colaborador" solo es relevante una vez que se realizó al menos un cierre de nómina. */
    hasClosedPeriod = false;

    /** Periodo distinto al abierto que se está consultando (banner "Mostrando periodo X"), vía ?period=id. */
    viewingPeriod: any = null;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private employeeService: EmployeeService,
        private payrollPeriodService: PayrollPeriodService,
        private permissionService: PermissionService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    /** "Cerrar nómina" es una acción de "Administrador de periodos", no de
     * "Listado de colaboradores" — aunque el botón que la dispara vive en
     * esta pantalla (el flujo de cierre arranca desde el listado de
     * colaboradores, pero es una operación sobre el periodo de nómina). */
    canCloseNomina(): boolean {
        return this.permissionService.canDo('Administrador de periodos', 'Cerrar nómina');
    }

    // ---- Filtros ----
    filterSearch = '';
    filterAvailability = '';
    filterDate = '';
    filterPayrollStatus = '';

    statusOptions = [
        { value: '', label: 'Seleccionar' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'stamped', label: 'Timbrado' },
        { value: 'error', label: 'Error' }
    ];

    private readonly availabilityMeta: { [key: string]: AvailabilityMeta } = {
        available: { label: 'Disponible', bg: '#e3fbe8', color: '#3c7e43' },
        on_vacation: { label: 'De vacaciones', bg: '#fff4de', color: '#b26a00' },
        on_duty: { label: 'En turno', bg: '#e3f2fd', color: '#1565c0' },
        absent: { label: 'Ausente', bg: '#fdecea', color: '#c0392b' }
    };

    availabilityOptions = [
        { value: '', label: 'Seleccionar' },
        { value: 'available', label: 'Disponible' },
        { value: 'on_vacation', label: 'De vacaciones' },
        { value: 'on_duty', label: 'En turno' },
        { value: 'absent', label: 'Ausente' }
    ];

    private filtersChanged$ = new Subject<void>();

    // ---- Grid ----
    private gridApi!: GridApi;
    public rowData: any = [];

    columnDefs = [
        {
            headerName: 'N°', width: 80, sortable: false, filter: false, resizable: false,
            valueGetter: (params: any) => this.getRowNumber(params)
        },
        {
            field: 'full_name', headerName: 'Colaborador', flex: 1.4, sortable: false, filter: false,
            cellRenderer: (params: any) => `<a href="javascript:void(0)" class="employee-name-link">${this.getFullName(params.data)}</a>`
        },
        { field: 'phone_1', headerName: 'Celular', flex: 1, sortable: false, filter: false },
        {
            field: 'availability', headerName: 'Disponibilidad', flex: 1, sortable: false, filter: false,
            cellRenderer: (params: any) => this.renderAvailabilityBadge(params.value)
        },
        {
            field: 'vacation_start_date', headerName: 'Inicio vacaciones', flex: 1, sortable: false, filter: false,
            valueFormatter: (params: any) => this.formatDate(params.value)
        },
        {
            field: 'leave_date', headerName: 'Fecha permiso', flex: 1, sortable: false, filter: false,
            valueFormatter: (params: any) => this.formatDate(params.value)
        },
        {
            field: 'hire_date', headerName: 'Incorporación', flex: 1, sortable: false, filter: false,
            valueFormatter: (params: any) => this.formatDate(params.value)
        },
        {
            headerName: 'Acciones', width: 120, sortable: false, filter: false, resizable: false,
            cellRenderer: EmployeeActionsComponent
        }
    ];

    public loadingCellRenderer: any = LoadingGridComponent;
    public loadingCellRendererParams: any = {
        loadingMessage: 'Espere un momento...',
    };

    public defaultColDef = {
        sortable: false,
        filter: false,
        resizable: true,
    };

    public noRowsTemplate = '<span style="padding: 24px; color: #6b7280;">No se encontraron colaboradores</span>';

    // ---- Paginación ----
    public pageSize = 12;
    public pageSizeOptions: number[] = [10, 12, 25, 50];
    public currentPage = 1;
    public lastPage = 1;
    public totalRows = 0;

    ngOnInit(): void {
        this.filtersChanged$.pipe(debounceTime(350)).subscribe(() => {
            this.currentPage = 1;
            this.fetchData();
        });
        this.checkOpenPeriod();
        this.checkClosedStatus();

        this.route.queryParams.subscribe((qp) => {
            const periodId = qp['period'];
            if (periodId) {
                this.payrollPeriodService.show(periodId).subscribe({
                    next: (period: any) => {
                        this.viewingPeriod = period;
                        if (this.gridApi) this.fetchData();
                    },
                    error: () => {
                        this.viewingPeriod = null;
                    }
                });
            } else {
                this.viewingPeriod = null;
                if (this.gridApi) this.fetchData();
            }
        });
    }

    volverAlPeriodoActual(): void {
        this.router.navigate(['/employee/list']);
    }

    checkOpenPeriod(): void {
        this.payrollPeriodService.current().subscribe({
            next: () => this.hasOpenPeriod = true,
            error: () => this.hasOpenPeriod = false
        });
    }

    checkClosedStatus(): void {
        this.payrollPeriodService.closedStatus().subscribe({
            next: (resp: any) => this.hasClosedPeriod = !!resp?.has_closed,
            error: () => this.hasClosedPeriod = false
        });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        // ag-grid no ejecuta correctamente los cellRenderer (de función o de
        // componente) en el primer render cuando columnDefs llega vía el
        // binding [columnDefs] de Angular; el setTimeout empuja la
        // reaplicación fuera del ciclo de detección de cambios en curso
        // (mismo bug ya documentado en payroll-receipts-list/payroll-list).
        setTimeout(() => params.api.setColumnDefs(this.columnDefs));
        this.fetchData();
    }

    onFiltersChanged() {
        this.filtersChanged$.next();
    }

    fetchData() {
        this.spinner.show();
        const params: any = {
            page: this.currentPage,
            per_page: this.pageSize,
        };
        if (this.filterSearch) params.search = this.filterSearch;
        if (this.filterAvailability) params.availability = this.filterAvailability;
        if (this.filterPayrollStatus) params.payroll_status = this.filterPayrollStatus;
        if (this.viewingPeriod?.id) params.period_id = this.viewingPeriod.id;
        if (this.filterDate) {
            // El diseño solo contempla un único filtro de fecha; se envía como
            // rango de un solo día ya que la API admite date_from / date_to.
            params.date_from = this.filterDate;
            params.date_to = this.filterDate;
        }

        this.employeeService.list(params).subscribe({
            next: (resp: any) => {
                this.rowData = Array.isArray(resp?.data) ? resp.data : [];
                this.currentPage = resp?.current_page || this.currentPage;
                this.lastPage = resp?.last_page || 1;
                this.totalRows = resp?.total ?? this.rowData.length;
                this.spinner.hide();
                setTimeout(() => this.gridApi?.setColumnDefs(this.columnDefs));
            },
            error: () => {
                this.rowData = [];
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el listado de colaboradores', icon: 'error' });
            }
        });
    }

    refreshGrid() {
        this.fetchData();
    }

    onPageSizeChanged(newSize: number | string) {
        const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;
        if (!isNaN(size)) {
            this.pageSize = size;
            this.currentPage = 1;
            this.fetchData();
        }
    }

    nextPage() {
        if (this.currentPage < this.lastPage) {
            this.currentPage += 1;
            this.fetchData();
        }
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage -= 1;
            this.fetchData();
        }
    }

    onCellClicked(event: any) {
        if (event?.colDef?.field === 'full_name' && event?.data?.id) {
            this.router.navigateByUrl(`/employee/${event.data.id}`);
        }
    }

    getRowNumber(params: any): number {
        const idx = params?.node?.rowIndex ?? 0;
        return (this.currentPage - 1) * this.pageSize + idx + 1;
    }

    getFullName(data: any): string {
        const parts = [data?.first_name, data?.last_name, data?.mother_last_name].filter(Boolean);
        return parts.join(' ') || '--';
    }

    renderAvailabilityBadge(value: string): string {
        const meta = this.availabilityMeta[value];
        if (!meta) return '';
        return `<span class="employee-availability-badge" style="background:${meta.bg};color:${meta.color}">${meta.label}</span>`;
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    // ---- Modal dar de baja ----
    openTerminationModal(data: any): void {
        if (!data?.id) return;
        if (data.status === 'terminated') {
            this.utils.alertSwal({ title: 'Colaborador dado de baja', text: 'Este colaborador ya se encuentra dado de baja.', icon: 'info' });
            return;
        }
        this.terminationModal.open({ id: data.id, fullName: this.getFullName(data) });
    }

    onEmployeeTerminated(event: TerminationEvent): void {
        this.fetchData();
    }

    // ---- Modal cierre total de nómina ----
    openCierreNominaModal(): void {
        this.spinner.show();
        this.payrollPeriodService.current().subscribe({
            next: (period: any) => {
                this.spinner.hide();
                this.cierreNominaModal.open({ id: period.id, period_date: period.period_date });
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Sin periodo abierto', text: 'No hay ningún periodo de nómina abierto para cerrar', icon: 'info' });
            }
        });
    }

    onNominaCerrada(event: CierreNominaEvent): void {
        // Aspel NOI: tras cerrar el periodo actual, el siguiente paso es
        // revisar y timbrar los recibos de los colaboradores — navega a
        // Recibos electrónicos con la fecha del periodo recién cerrado.
        const periodDate = event?.response?.period_date;
        if (periodDate) {
            const isoDate = String(periodDate).split('T')[0].split(' ')[0];
            this.router.navigate(['/payroll-receipts'], { queryParams: { period: isoDate } });
        } else {
            this.router.navigateByUrl('/payroll-receipts');
        }
    }
}
